import { FC } from 'react';
import FilterDropdown from '../custom-input/FilterDropdown';
import ValidatedInput from '../custom-input/ValidatedInput';
import { PaymentActions, PayNowPaymentMethods, ShippingPaymentMethods, Validators } from '../../types/enums';
import { PaymentMethod } from '../../types/interfaces/paymentMethod.interface';
import { ADDRESS_CONSTANTS, PAYMENT_CONSTANTS } from '../../constants/common';
import { COUNTRIES_WITH_STATES, COUNTRY_CODES } from '../../constants/data';
import './addPaymentMethodModal.scss';

type BillingAddressProps = {
  paymentMethod: PaymentMethod;
  validate: boolean;
  buttonAction: string;
  paymentMethodType: string;
  setPaymentMethod: (paymentMethod: PaymentMethod) => void;
  useMarketplaceAchAddressLayout?: boolean;
};

const BillingAddress: FC<BillingAddressProps> = ({
  paymentMethod,
  validate,
  buttonAction,
  paymentMethodType,
  setPaymentMethod,
  useMarketplaceAchAddressLayout = false,
}) => {
  const isExistingMethodSelected = () => {
    return (
      (buttonAction === PaymentActions.PAY_NOW_METHOD && paymentMethodType === PayNowPaymentMethods.EXISTING) ||
      (buttonAction === PaymentActions.SHIPPING_PAYMENT && paymentMethodType === ShippingPaymentMethods.CARD_ON_FILE)
    );
  };

  const selectedCountry = COUNTRY_CODES.find(country => country.value === paymentMethod?.country?.value) ?? {
    name: paymentMethod?.country?.value ?? '',
    value: paymentMethod?.country?.value ?? '',
  };
  const stateOptions = paymentMethod?.country?.value ? COUNTRIES_WITH_STATES[paymentMethod.country.value] ?? [] : [];
  const selectedState = stateOptions.find(state => state.value === paymentMethod?.region?.value) ?? {
    name: paymentMethod?.region?.value ?? '',
    value: paymentMethod?.region?.value ?? '',
  };

  const renderCityField = () => (
    <div className="input">
      <ValidatedInput
        label={ADDRESS_CONSTANTS.CITY}
        validatedStateForAutoFill={paymentMethod.city}
        validate={validate}
        setValidatedState={event => {
          setPaymentMethod({
            ...paymentMethod,
            city: { value: event?.value?.replace(/\s+/g, ' '), valid: event?.valid && event?.value?.trim().length > 0 },
          });
        }}
        disabled={isExistingMethodSelected()}
        validators={[Validators.REQUIRED]}
      />
    </div>
  );

  const renderRegionField = () => (
    <div className="input">
      {useMarketplaceAchAddressLayout ? (
        <>
          <label className="validated-input-label">{ADDRESS_CONSTANTS.STATE_REGION}</label>
          <FilterDropdown
            placeholder="Select"
            options={stateOptions}
            value={selectedState}
            validate={validate}
            disabled={isExistingMethodSelected() || !paymentMethod?.country?.value}
            onChange={event => {
              setPaymentMethod({
                ...paymentMethod,
                region: { value: event.value, valid: Boolean(event.value) },
              });
            }}
          />
        </>
      ) : (
        <ValidatedInput
          label={ADDRESS_CONSTANTS.STATE_REGION}
          validatedStateForAutoFill={paymentMethod?.region}
          validate={validate}
          setValidatedState={event => {
            setPaymentMethod({
              ...paymentMethod,
              region: { value: event?.value?.replace(/\s+/g, ' '), valid: event?.valid && event?.value?.trim().length > 0 },
            });
          }}
          disabled={isExistingMethodSelected()}
          validators={[Validators.REQUIRED]}
        />
      )}
    </div>
  );

  const renderPostalCodeField = () => (
    <div className="input">
      <ValidatedInput
        label={ADDRESS_CONSTANTS.ZIP_CODE}
        validatedStateForAutoFill={paymentMethod?.postalCode}
        validate={validate}
        setValidatedState={event => {
          setPaymentMethod({ ...paymentMethod, postalCode: { value: event?.value, valid: event?.valid } });
        }}
        disabled={isExistingMethodSelected()}
        validators={[Validators.REQUIRED, Validators.ZIP_CODE]}
      />
    </div>
  );

  const renderCountryField = () => (
    <div className="input">
      {useMarketplaceAchAddressLayout ? (
        <>
          <label className="validated-input-label">{ADDRESS_CONSTANTS.COUNTRY}</label>
          <FilterDropdown
            placeholder="Select"
            options={COUNTRY_CODES}
            value={selectedCountry}
            validate={validate}
            disabled={isExistingMethodSelected()}
            onChange={event => {
              setPaymentMethod({
                ...paymentMethod,
                country: { value: event.value, valid: Boolean(event.value) },
                region: { value: '', valid: false },
              });
            }}
          />
        </>
      ) : (
        <ValidatedInput
          label={ADDRESS_CONSTANTS.COUNTRY}
          validatedStateForAutoFill={paymentMethod?.country}
          validate={validate}
          setValidatedState={event => {
            setPaymentMethod({
              ...paymentMethod,
              country: { value: event?.value?.replace(/\s+/g, ' '), valid: event?.valid && event?.value?.trim().length > 0 },
            });
          }}
          disabled={isExistingMethodSelected()}
          validators={[Validators.REQUIRED]}
        />
      )}
    </div>
  );

  return (
    <div className="billing-address">
      <h3>{PAYMENT_CONSTANTS.BILLING_ADDRESS}</h3>
      <div className="input">
        <ValidatedInput
          label={ADDRESS_CONSTANTS.ADDRESS}
          validatedStateForAutoFill={paymentMethod?.address}
          setValidatedState={event => {
            setPaymentMethod({
              ...paymentMethod,
              address: { value: event?.value?.replace(/\s+/g, ' '), valid: event?.valid && event?.value?.trim().length > 0 },
            });
          }}
          validate={validate}
          disabled={isExistingMethodSelected()}
          validators={[Validators.REQUIRED]}
        />
      </div>
      <div className="input-row">
        {useMarketplaceAchAddressLayout ? (
          <>
            {renderCountryField()}
            {renderRegionField()}
            {renderCityField()}
            {renderPostalCodeField()}
          </>
        ) : (
          <>
            {renderCityField()}
            {renderRegionField()}
            {renderPostalCodeField()}
            {renderCountryField()}
          </>
        )}
      </div>
    </div>
  );
};

export default BillingAddress;
